import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Typography } from "antd";
import { CSSProperties } from "react";
import { useCustomerReferral } from "hooks/queries/referral";
import { translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { LoyaltyRewardType } from "shared/types/enum";
import { localizeRewardSummary } from "shared/utils/loyalty";
import { textPx } from "shared/constants/zoom";

/**
 * One line in the Upgrade and the discount-voucher purchase pop-ups saying
 * that this sale completes the client's pending referral. Both now convert it
 * (System Review S19), but neither can apply the client's intro discount, so
 * that discount is issued as a voucher for a later purchase; the admin should
 * see that before confirming rather than find a voucher afterwards (Andrew,
 * 2026-10-02, option B). Mirrors the server's gates: a pending referral, and
 * the tier's BASE price at or above the programme minimum.
 */
const ReferralCompletesNote = ({
  customerId,
  open,
  basePrice,
  kind,
  style,
}: {
  customerId?: string;
  /** The pop-up is open; the referral is only fetched then. */
  open: boolean;
  /** The chosen tier's catalogue price; nothing shows until one is chosen. */
  basePrice?: number;
  kind: "upgrade" | "purchase";
  /** Spacing from the pop-up around it. */
  style?: CSSProperties;
}) => {
  const { data } = useCustomerReferral(customerId, open);
  const intro = data?.pending_intro;
  if (!intro || basePrice === undefined) return null;
  if (
    intro.min_purchase_amount != null &&
    basePrice < intro.min_purchase_amount
  ) {
    return null;
  }
  const reward = localizeRewardSummary(intro.friend_offer);
  const variant =
    !intro.friend_reward || !reward
      ? "plain"
      : intro.friend_reward.reward_type === LoyaltyRewardType.Discount
      ? "discount"
      : "credit";
  return (
    <Flex
      gap={8}
      align="flex-start"
      style={{
        padding: "10px 12px",
        borderRadius: 10,
        background: colors.bgSoft,
        border: `1px solid ${colors.line}`,
        ...style,
      }}
    >
      <InfoCircleOutlined
        aria-hidden
        style={{ color: colors.navy, marginTop: 3 }}
      />
      <Typography.Text style={{ fontSize: textPx(13), color: colors.ink }}>
        {translateWithVars(`referral.completes.${kind}_${variant}`, {
          referrer: intro.referrer_name,
          reward: reward ?? "",
        })}
      </Typography.Text>
    </Flex>
  );
};

export default ReferralCompletesNote;
